import qs from 'qs';
import oikosApi from '../../services/oikos';

const TOKEN_REGEX = /{{\s*([a-zA-Z0-9_]+)\s*}}/g;

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatDate = (value) => {
  if (!value) return '';

  const isoDate = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) return `${isoDate[3]}/${isoDate[2]}/${isoDate[1]}`;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(date);
};

const formatValue = (value, format, sourceType) => {
  if (format === 'DD/MM/YYYY') return formatDate(value);
  if (format === 'html_link') {
    if (sourceType !== 'resolvedor') {
      throw new Error('O formato html_link só pode ser usado por um resolvedor de mensagem.');
    }
    return String(value);
  }
  return escapeHtml(value);
};

const getFornecedorNome = (fornecedor = {}) => (
  fornecedor.nome
  || fornecedor.pf_nomeExibicao
  || fornecedor.pj_nomefanta
  || fornecedor.pj_razaosocial
  || ''
);

const renderLinkEnviarProposta = (contexto = {}) => {
  const url = contexto?.links?.enviarProposta;
  if (!url) return '';

  return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">Enviar Proposta</a>`;
};

const RESOLVEDORES = {
  'fornecedor.nome_exibicao': (contexto) => getFornecedorNome(contexto.fornecedor),
  'pedidoCompra.link_enviar_proposta': renderLinkEnviarProposta,
};

const getContextEntity = (contexto, entidade) => {
  const entities = {
    pedidoCompra: contexto.pedidoCompra,
    compraCotacao: contexto.compraCotacao,
    fornecedor: contexto.fornecedor,
    solicitacao: contexto.solicitacao,
  };

  return entities[entidade];
};

const isRequired = (variavel) => Number(variavel.mensagemTemplateVariavel_obrigatoria) === 1;

const isEmpty = (value) => value == null || String(value).trim() === '';

const getVariableValue = (variavel, contexto) => {
  const sourceType = variavel.mensagemTemplateVariavel_tipoFonte;

  if (sourceType === 'campo') {
    const entity = getContextEntity(
      contexto,
      variavel.mensagemTemplateVariavel_entidadeFonte,
    );
    const column = variavel.mensagemTemplateVariavel_colunaFonte;
    return entity && column ? entity[column] : undefined;
  }

  if (sourceType === 'resolvedor') {
    const resolverName = variavel.mensagemTemplateVariavel_resolvedor;
    const resolver = RESOLVEDORES[resolverName];
    if (!resolver) {
      throw new Error(`Resolvedor de mensagem não suportado: ${resolverName || '(não informado)'}.`);
    }
    return resolver(contexto);
  }

  throw new Error(`Tipo de fonte de mensagem não suportado: ${sourceType || '(não informado)'}.`);
};

/**
 * Substitui os tokens registrados para uma versão de template pelo contexto informado.
 * Esta função não consulta a API e pode ser usada para pré-visualizações e testes.
 */
export const renderizarCorpoMensagemTemplate = ({
  corpoHtml,
  variaveis = [],
  contexto = {},
}) => {
  const variableByToken = new Map(
    variaveis.map((variavel) => [variavel.mensagemTemplateVariavel_token, variavel]),
  );
  const missingTokens = new Set();

  const html = String(corpoHtml || '').replace(TOKEN_REGEX, (token, tokenName) => {
    const variavel = variableByToken.get(tokenName);
    if (!variavel) {
      missingTokens.add(tokenName);
      return token;
    }

    let value;
    try {
      value = getVariableValue(variavel, contexto);
    } catch (error) {
      missingTokens.add(tokenName);
      return token;
    }

    if (isEmpty(value)) {
      value = variavel.mensagemTemplateVariavel_valorPadrao;
    }

    if (isEmpty(value) && isRequired(variavel)) {
      missingTokens.add(tokenName);
      return token;
    }

    return formatValue(
      value,
      variavel.mensagemTemplateVariavel_formato,
      variavel.mensagemTemplateVariavel_tipoFonte,
    );
  });

  if (missingTokens.size > 0) {
    throw new Error(`Não foi possível renderizar as variáveis obrigatórias ou não reconhecidas: ${[...missingTokens].join(', ')}.`);
  }

  return html;
};

const getRows = async (table, data, fields, order) => {
  const response = await oikosApi.get(`/?${qs.stringify({
    action: 'get',
    table,
    data,
    fields,
    ...(order ? { order } : {}),
  })}`);
  return response?.data || [];
};

/**
 * Carrega a versão publicada atual identificada pelo hand do template.
 */
export const carregarMensagemTemplatePublicada = async (hand) => {
  const normalizedHand = String(hand || '').trim();
  if (!normalizedHand) throw new Error('Informe o identificador do template de mensagem.');

  const templates = await getRows('mensagemTemplate', {
    mensagemTemplate_hand: normalizedHand,
    mensagemTemplate_ativo: 1,
  });
  const template = templates[0];
  const versionId = Number(template?.mensagemTemplateVersao_idAtual);

  if (!template || !versionId) {
    throw new Error(`Template de mensagem publicado não encontrado: ${normalizedHand}.`);
  }

  const versions = await getRows('mensagemTemplateVersao', {
    mensagemTemplateVersao_id: versionId,
    mensagemTemplateVersao_status: 'publicado',
    mensagemTemplateVersao_ativo: 1,
  });
  const versao = versions[0];
  if (!versao) {
    throw new Error(`Versão publicada do template de mensagem não encontrada: ${normalizedHand}.`);
  }

  const variaveis = await getRows('mensagemTemplateVariavel', {
    mensagemTemplateVersao_id: versionId,
    mensagemTemplateVariavel_ativo: 1,
  }, undefined, 'mensagemTemplateVariavel_ord ASC');

  return { template, versao, variaveis };
};

/**
 * Retorna o HTML do template publicado com todos os tokens substituídos.
 */
export const renderizarMensagemTemplate = async (hand, contexto = {}) => {
  const { versao, variaveis } = await carregarMensagemTemplatePublicada(hand);
  return renderizarCorpoMensagemTemplate({
    corpoHtml: versao.mensagemTemplateVersao_corpoHtml,
    variaveis,
    contexto,
  });
};

